import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Radio,
  message,
  Cascader,
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { Lead } from '../../services/lead';
import { getAssignableUsers, UserAssignee } from '../../services/user';
import {
  getEntityCollaborators,
  addCollaborator,
  removeCollaborator,
  Collaborator,
} from '../../services/collaborator';
import dayjs from 'dayjs';
import AttachmentUpload from '../AttachmentUpload';
import { DATA_SOURCE_OPTIONS } from '../../constants/leadConstants';
import { REGION_DATA } from '../../utils/regionData';
import { getCurrentUser } from '../../services/auth';
import { UserRole } from '../../constants/user';

const { TextArea } = Input;
const { Option } = Select;

interface LeadDetailFormProps {
  lead: Lead;
  editMode: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (values: any) => void;
  canEdit?: boolean;
}

const LeadDetailForm: React.FC<LeadDetailFormProps> = ({
  lead,
  editMode,
  onEdit,
  onCancel,
  onSave,
  canEdit = true,
}) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<UserAssignee[]>([]);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [collaboratorIds, setCollaboratorIds] = useState<number[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // Load current user and check admin status
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = await getCurrentUser();
        setIsAdmin(user.role === UserRole.ADMIN);
      } catch (error) {
        console.error('Failed to load current user:', error);
      }
    };
    loadCurrentUser();
  }, []);

  // Load users for assignment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await getAssignableUsers({ limit: 100 });
        setUsers(response || []);
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    };
    loadUsers();
  }, []);

  // Load collaborators
  useEffect(() => {
    const loadCollaborators = async () => {
      if (!lead?.id) return;
      try {
        const response = await getEntityCollaborators('lead', lead.id);
        setCollaborators(response || []);
        setCollaboratorIds(response.map(c => c.user_id));
      } catch (error) {
        console.error('Failed to load collaborators:', error);
      }
    };
    loadCollaborators();
  }, [lead?.id]);

  // Initialize form values
  useEffect(() => {
    if (lead) {
      // 构建地区数组
      const region = [];
      if (lead.province) region.push(lead.province);
      if (lead.city) region.push(lead.city);
      if (lead.district) region.push(lead.district);

      form.setFieldsValue({
        ...lead,
        region: region.length > 0 ? region : undefined,
        next_follow_time: lead.next_follow_time
          ? dayjs(lead.next_follow_time)
          : undefined,
        visit_time: lead.visit_time ? dayjs(lead.visit_time) : undefined,
      });
    }
  }, [lead, form]);

  // Handle collaborator change
  const handleCollaboratorChange = async (selectedUserIds: number[]) => {
    if (!lead?.id) return;

    try {
      // Find added collaborators
      const addedIds = selectedUserIds.filter(id => !collaboratorIds.includes(id));
      for (const userId of addedIds) {
        await addCollaborator({
          target_type: 'lead',
          target_id: lead.id,
          user_id: userId,
        });
      }

      // Find removed collaborators
      const removedIds = collaboratorIds.filter(id => !selectedUserIds.includes(id));
      for (const userId of removedIds) {
        await removeCollaborator(userId, 'lead', lead.id);
      }

      // Reload collaborators
      const response = await getEntityCollaborators('lead', lead.id);
      setCollaborators(response || []);
      setCollaboratorIds(response.map(c => c.user_id));

      message.success('协作人更新成功');
    } catch (error) {
      message.error('协作人更新失败');
      console.error('Failed to update collaborators:', error);
    }
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Handle region data
      if (values.region && Array.isArray(values.region)) {
        values.province = values.region[0] || null;
        values.city = values.region[1] || null;
        values.district = values.region[2] || null;
        delete values.region;
      }
      
      // Format dates
      const formattedValues = {
        ...values,
        next_follow_time: values.next_follow_time
          ? values.next_follow_time.format('YYYY-MM-DD HH:mm:ss')
          : null,
        visit_time: values.visit_time
          ? values.visit_time.format('YYYY-MM-DD HH:mm:ss')
          : null,
      };

      // Remove collaborators from form values (handled separately)
      delete formattedValues.collaborators;

      onSave(formattedValues);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  return (
    <div className="lead-detail-form">
      <div className="section-header">
        <div className="section-title">详细信息</div>
        {!editMode && canEdit && (
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={onEdit}
          >
            编辑
          </Button>
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        disabled={!editMode}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="contact_name"
              label={
                <Space size={4}>
                  <UserOutlined />
                  <span>联系人</span>
                </Space>
              }
              rules={[{ required: true, message: '请输入联系人' }]}
            >
              <Input placeholder="请输入联系人" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone"
              label={
                <Space size={4}>
                  <PhoneOutlined />
                  <span>手机号</span>
                </Space>
              }
              rules={[{ required: true, message: '请输入手机号' }]}
            >
              <Input placeholder="请输入手机号" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="case_description"
          label="描述"
        >
          <TextArea
            rows={3}
            placeholder="请输入描述信息"
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="备注信息"
        >
          <TextArea
            rows={2}
            placeholder="请输入备注信息"
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="协作人"
              tooltip={!isAdmin ? "只有管理员可以设置协作人" : undefined}
            >
              <Select
                mode="multiple"
                placeholder={isAdmin ? "请选择协作人" : "仅管理员可设置"}
                value={collaboratorIds}
                onChange={handleCollaboratorChange}
                showSearch
                optionFilterProp="children"
                disabled={!editMode || !isAdmin}
                maxTagCount="responsive"
              >
                {users.map(user => (
                  <Option key={user.id} value={user.id}>
                    {user.real_name || `用户${user.id}`}
                    {user.department && ` (${user.department})`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="data_source"
              label="数据来源"
              tooltip={!isAdmin ? "仅管理员可修改数据来源" : undefined}
            >
              <Select 
                placeholder="请选择数据来源" 
                allowClear
                disabled={!editMode || !isAdmin}
              >
                {DATA_SOURCE_OPTIONS.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="lead_type"
              label="线索类型"
            >
              <Input placeholder="请输入线索类型" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="urgency_level"
              label="紧急程度"
            >
              <Select placeholder="请选择紧急程度" allowClear>
                <Option value="A类">A类</Option>
                <Option value="B类">B类</Option>
                <Option value="C类">C类</Option>
                <Option value="D类">D类</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="follow_status"
              label="跟进意向"
            >
              <Select placeholder="请选择跟进意向" allowClear>
                <Option value="未联系">未联系</Option>
                <Option value="未接通">未接通</Option>
                <Option value="不需要">不需要</Option>
                <Option value="无人接听">无人接听</Option>
                <Option value="无效">无效</Option>
                <Option value="空号">空号</Option>
                <Option value="停机">停机</Option>
                <Option value="稍后联系">稍后联系</Option>
                <Option value="重复">重复</Option>
                <Option value="客诉">客诉</Option>
                <Option value="已加微信">已加微信</Option>
                <Option value="已加企微">已加企微</Option>
                <Option value="已付咨询费">已付咨询费</Option>
                <Option value="邀约到所">邀约到所</Option>
                <Option value="已成交">已成交</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* 预留空位，保持布局对称 */}
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="region"
              label="所在地区"
              tooltip={!isAdmin ? "仅管理员可修改所在地区" : undefined}
            >
              {editMode && isAdmin ? (
                <Cascader
                  options={REGION_DATA}
                  placeholder="请选择省/市/区"
                  changeOnSelect
                  showSearch
                />
              ) : (
                <Input
                  value={
                    lead.province || lead.city || lead.district
                      ? `${lead.province || ''}${lead.city || ''}${lead.district || ''}`
                      : '-'
                  }
                  disabled
                  style={{ color: lead.province || lead.city || lead.district ? '#1890ff' : '#999' }}
                />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="visit_time"
              label="到访时间"
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                placeholder="请选择到访时间"
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="next_follow_time"
              label="下次跟进时间"
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                placeholder="请选择下次跟进时间"
                format="YYYY-MM-DD HH:mm"
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="附件">
          <AttachmentUpload
            entityType="lead"
            entityId={lead.id}
            maxCount={10}
            disabled={!editMode}
          />
        </Form.Item>

        {editMode && (
          <Form.Item>
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSubmit}
              >
                保存
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={onCancel}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        )}
      </Form>
    </div>
  );
};

export default LeadDetailForm;
